import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import supabase from '../config/supabase';
import { queryCountSessions, directQuery } from '../utils/directFetch';

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const SessionsCalendar = ({ onSessionSelect, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');

  useEffect(() => {
    loadSessionsForCalendar();
  }, []);

  const loadSessionsForCalendar = async () => {
    console.log('🔍 [SessionsCalendar] Starting to load sessions...');
    setLoading(true);
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      console.log('📋 [SessionsCalendar] Getting user and store from localStorage...');
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('❌ [SessionsCalendar] No user or store found in localStorage');
        setLoading(false);
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [SessionsCalendar] Found user in localStorage:', user.email, 'store:', storeId);

      // Load all sessions for calendar view (not just active ones) - USE DIRECT FETCH
      console.log('🔍 [DB-CALL-1] Starting count_sessions query for calendar, store:', storeId);

      const { data: sessions, error } = await queryCountSessions(storeId, {
        order: { column: 'session_date', ascending: true }
      });

      if (error) {
        console.error('❌ [DB-CALL-1] Error loading sessions for calendar:', error);
        return;
      }

      // Also load scheduled sessions from auto-schedules - USE DIRECT FETCH
      console.log('🔍 [DB-CALL-2] Starting scheduled_count_sessions query for calendar, store:', storeId);

      // Note: Direct fetch doesn't support complex joins, so we'll load scheduled sessions separately
      // and then fetch related count_sessions data if needed
      const { data: scheduledSessions, error: scheduledError } = await directQuery('scheduled_count_sessions', {
        order: { column: 'scheduled_date', ascending: true }
      });

      console.log('✅ [DB-CALL-2] scheduled_count_sessions completed. Records:', scheduledSessions?.length || 0, 'Error:', scheduledError?.message || 'none');

      if (scheduledError) {
        console.error('Error loading scheduled sessions for calendar:', scheduledError);
        console.log('🔍 [SessionsCalendar] Continuing without scheduled sessions (table may not exist yet)');
        // Continue without scheduled sessions rather than failing completely
      }

      console.log('🔍 [SessionsCalendar] Loaded', sessions?.length || 0, 'regular sessions and', scheduledSessions?.length || 0, 'scheduled sessions');

      // Combine regular sessions and scheduled sessions
      const allSessions = [
        ...(sessions || []),
        ...(scheduledSessions || []).map(scheduledSession => ({
          // Map scheduled session fields to match regular session structure
          id: scheduledSession.id,
          name: scheduledSession.count_sessions?.name || 'Scheduled Session',
          session_date: scheduledSession.scheduled_date, // Map scheduled_date to session_date
          status: scheduledSession.count_sessions?.status || 'scheduled',
          count_commence_time: scheduledSession.count_sessions?.count_commence_time || '9:00 AM',
          visible_from_date: scheduledSession.visible_from_date,
          store_id: scheduledSession.count_sessions?.store_id,
          // Mark as scheduled for styling purposes
          isScheduled: true
        }))
      ];

      console.log('🔍 [SessionsCalendar] Combined sessions:', allSessions.length);
      console.log('🔍 [SessionsCalendar] Sample session:', allSessions[0]);

      // Transform sessions into calendar events
      const calendarEvents = allSessions.map(session => {
        // For scheduled sessions, use visible_from_date if available
        // This allows "show X days before" functionality to work correctly
        const displayDate = session.visible_from_date ? session.visible_from_date : session.session_date;
        const sessionDate = new Date(displayDate);
        
        // If we have a count_commence_time, use it; otherwise default to 9 AM
        let startTime = sessionDate;
        if (session.count_commence_time) {
          // Parse the time and combine with the session date
          const [time, period] = session.count_commence_time.split(' ');
          const [hours, minutes] = time.split(':');
          let hour24 = parseInt(hours);
          
          if (period === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (period === 'AM' && hour24 === 12) {
            hour24 = 0;
          }
          
          startTime = new Date(sessionDate);
          startTime.setHours(hour24, parseInt(minutes), 0, 0);
        } else {
          startTime = new Date(sessionDate);
          startTime.setHours(9, 0, 0, 0); // Default to 9 AM
        }

        // End time is 2 hours after start (typical count session duration)
        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + 2);

        // Determine color based on status
        let backgroundColor = '#8A7A6B'; // Default brown
        let textColor = 'white';
        
        switch (session.status) {
          case 'draft':
            backgroundColor = '#6C757D'; // Gray
            break;
          case 'scheduled':
            backgroundColor = '#8A7A6B'; // Brown for auto-scheduled sessions
            break;
          case 'in_progress':
            backgroundColor = '#0D6EFD'; // Blue
            break;
          case 'completed':
            backgroundColor = '#198754'; // Green
            break;
          default:
            backgroundColor = '#8A7A6B'; // Brown
        }

        return {
          id: session.id,
          title: session.name || `Count Session ${session.id}`,
          start: startTime,
          end: endTime,
          resource: session, // Store the full session data
          style: {
            backgroundColor,
            color: textColor,
            border: `1px solid ${backgroundColor}`,
            borderRadius: '4px'
          }
        };
      });

      console.log('🔍 [SessionsCalendar] Generated calendar events:', calendarEvents.length);
      console.log('🔍 [SessionsCalendar] Sample calendar event:', calendarEvents[0]);
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error loading sessions for calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    if (onSessionSelect) {
      onSessionSelect(event.resource);
    }
  };

  const handleSelectSlot = ({ start, end }) => {
    // Could be used to create new sessions by clicking on empty calendar slots
    console.log('Selected time slot:', { start, end });
  };

  const eventStyleGetter = (event) => {
    return {
      style: event.style
    };
  };

  const CustomEvent = ({ event }) => {
    const containerStyle = {
      fontSize: '0.8rem', 
      fontWeight: '500',
      padding: '2px 4px',
      cursor: 'pointer',
      height: '100%',
      maxHeight: '100%',
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      overflow: 'hidden'
    };

    const titleStyle = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: '1.2',
      fontSize: '0.75rem',
      fontWeight: '500'
    };

    const statusStyle = {
      fontSize: '0.65rem', 
      opacity: 0.8,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: '1.1',
      marginTop: '1px'
    };

    return (
      <div style={containerStyle} title={`Click to open ${event.title} (${event.resource.status})`}>
        <div style={titleStyle}>{event.title}</div>
        <div style={statusStyle}>{event.resource.status}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border" style={{ color: '#8A7A6B' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{
      backgroundColor: '#F5F3F0',
      border: '1px solid #D4C4B0'
    }}>
      <style>{`
        .rbc-event,
        .rbc-event-content {
          box-sizing: border-box !important;
          max-width: 100% !important;
          width: 100% !important;
          overflow: hidden !important;
        }
        
        .rbc-row-segment {
          overflow: hidden !important;
        }
        
        .rbc-month-view .rbc-event {
          max-height: 100% !important;
          height: auto !important;
          min-height: 18px !important;
        }
        
        .rbc-event > div {
          width: 100% !important;
          box-sizing: border-box !important;
        }
      `}</style>
      <div className="card-header" style={{ backgroundColor: 'transparent' }}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ color: '#5A4A42' }}>Count Sessions Calendar</h5>
          <div className="d-flex gap-2 align-items-center">
            <small style={{ color: '#8A7A6B', fontSize: '0.8rem' }}>
              <span className="badge me-2" style={{ backgroundColor: '#6C757D' }}>Draft</span>
              <span className="badge me-2" style={{ backgroundColor: '#0D6EFD' }}>In Progress</span>
              <span className="badge me-2" style={{ backgroundColor: '#198754' }}>Completed</span>
            </small>

          </div>
        </div>
      </div>
      <div className="card-body p-3">
        <div style={{ height: '600px' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%', color: '#5A4A42' }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            popup={true}
            popupOffset={30}
            views={['month', 'week', 'day', 'agenda']}
            view={view}
            onView={setView}
            eventPropGetter={eventStyleGetter}
            components={{
              event: CustomEvent
            }}
            step={30}
            timeslots={2}
            dayLayoutAlgorithm="no-overlap"
            formats={{
              dayHeaderFormat: 'MMM dd',
              dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, 'MMM dd', culture) + ' – ' + 
                localizer.format(end, 'MMM dd', culture),
              agendaDateFormat: 'MMM dd',
              agendaTimeFormat: 'h:mm a',
              agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, 'h:mm a', culture) + ' – ' + 
                localizer.format(end, 'h:mm a', culture),
            }}
            messages={{
              next: "Next",
              previous: "Previous", 
              today: "Today",
              month: "Month",
              week: "Week", 
              day: "Day",
              agenda: "Agenda",
              noEventsInRange: "No count sessions scheduled for this period.",
              showMore: total => `+${total} more sessions`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionsCalendar;